import { useClerk } from '@clerk/clerk-react'
import { useFlag } from '@unleash/proxy-client-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'

const INACTIVITY_TIMEOUT = 1000 * 60 * 10 // 10 minutes
const COUNTDOWN_S = 60
const TICK_MS = 10_000
const LS_ACTIVITY = 'inactivity:lastActivity'

export default function InactivityModal() {
  const { signOut } = useClerk()
  const isEnabled = useFlag('inactive-logout')

  const [open, setOpen] = useState(false)
  const [countdown, setCountdown] = useState(COUNTDOWN_S)
  const isOpen = useRef(false)
  const modalOpenedAt = useRef<number | null>(null)

  const handleStillHere = () => {
    setOpen(false)
    isOpen.current = false
    modalOpenedAt.current = null
    setCountdown(COUNTDOWN_S)
    localStorage.setItem(LS_ACTIVITY, Date.now().toString())
  }

  useEffect(() => {
    if (!isEnabled) return

    const onActivity = () => {
      if (isOpen.current) return
      localStorage.setItem(LS_ACTIVITY, Date.now().toString())
    }

    const inactivityCheck = setInterval(() => {
      if (isOpen.current) return
      const elapsed = Date.now() - Number(localStorage.getItem(LS_ACTIVITY) ?? Date.now())
      if (elapsed >= INACTIVITY_TIMEOUT) {
        isOpen.current = true
        modalOpenedAt.current = Date.now()
        setOpen(true)
      }
    }, TICK_MS)

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }))
    localStorage.setItem(LS_ACTIVITY, Date.now().toString())

    return () => {
      clearInterval(inactivityCheck)
      events.forEach(e => window.removeEventListener(e, onActivity))
    }
  }, [isEnabled])

  // Separate 1s tick — only active while the modal is open
  useEffect(() => {
    if (!open) return

    const countdownTick = setInterval(() => {
      const elapsed = Date.now() - Number(localStorage.getItem(LS_ACTIVITY) ?? Date.now())
      if (elapsed < INACTIVITY_TIMEOUT) {
        setOpen(false)
        isOpen.current = false
        modalOpenedAt.current = null
        setCountdown(COUNTDOWN_S)
        return
      }
      const remaining = Math.max(
        0,
        COUNTDOWN_S - Math.floor((Date.now() - (modalOpenedAt.current ?? Date.now())) / 1000)
      )
      setCountdown(remaining)
      if (remaining <= 0) signOut()
    }, 1000)

    return () => clearInterval(countdownTick)
  }, [open, signOut])

  if (!isEnabled) return null

  const isUrgent = countdown <= 10

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          handleStillHere()
        }
      }}
    >
      <DialogContent
        className='max-w-md gap-6'
        onInteractOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <span className='absolute right-4 top-4' />

        <DialogHeader className='gap-2'>
          <div className='flex items-center gap-3'>
            <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-custom-blue-100'>
              <svg
                className='size-5 text-custom-blue-600'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                strokeWidth={2}
                aria-hidden='true'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z'
                />
              </svg>
            </span>
            <DialogTitle className='text-custom-neutral-900 text-lg font-semibold'>Are you still there?</DialogTitle>
          </div>
          <DialogDescription className='text-custom-neutral-500 text-sm leading-relaxed'>
            You've been inactive for a while. For your security, you'll be signed out automatically.
          </DialogDescription>
        </DialogHeader>

        <div className='flex flex-col items-center gap-3 py-2'>
          <div className='relative flex size-20 items-center justify-center'>
            <svg className='absolute inset-0 -rotate-90' viewBox='0 0 80 80' aria-hidden='true'>
              <circle
                cx='40'
                cy='40'
                r='34'
                fill='none'
                stroke='currentColor'
                strokeWidth='5'
                className='text-custom-neutral-200'
              />
              <circle
                cx='40'
                cy='40'
                r='34'
                fill='none'
                stroke='currentColor'
                strokeWidth='5'
                strokeLinecap='round'
                strokeDasharray={2 * Math.PI * 34}
                strokeDashoffset={2 * Math.PI * 34 * (1 - countdown / COUNTDOWN_S)}
                className={`transition-all duration-1000 ${isUrgent ? 'text-destructive' : 'text-primary'}`}
              />
            </svg>
            <span
              className={`text-xl font-bold tabular-nums transition-colors duration-300 ${isUrgent ? 'text-destructive' : 'text-custom-neutral-900'}`}
            >
              {countdown}
            </span>
          </div>
          <p className='text-xs text-custom-neutral-400'>seconds until sign-out</p>
        </div>

        <DialogFooter className='flex-row gap-2 sm:justify-center'>
          <Button
            variant='outline'
            className='flex-1 rounded-2xl border-custom-neutral-200 text-custom-neutral-600 hover:bg-custom-gray-1'
            onClick={() => signOut()}
          >
            Sign out now
          </Button>
          <Button className='flex-1' onClick={handleStillHere}>
            I'm still here
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
